import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Loader2 } from 'lucide-react'
import { OnboardingWizard } from '../components/OnboardingWizard'
import { AuthSplitLayout } from '../components/AuthSplitLayout'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/useAuth'

export const Route = createFileRoute('/_AuthLayout/onboarding')({
  component: OnboardingPage,
})

function OnboardingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const { t } = useTranslation('auth')
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [initialData, setInitialData] = useState<{
    name?: string
  }>({})
  const [error, setError] = useState<string | null>(null)

  const email = user?.email ?? ''
  const name = user?.name.trim() ?? ''

  useEffect(() => {
    if (!isAuthenticated) {
      void navigate({ to: '/auth', replace: true })
      return
    }

    // No profile yet: AuthProvider is loading it from /auth/get-session, or
    // the JWT's claims will provide the email once minted. Keep the spinner.
    if (!user) return

    if (!email) {
      setError(t('onboarding.noEmail'))
      return
    }

    setUserEmail(email)
    setInitialData({ name })
  }, [isAuthenticated, user, email, name, navigate, t])

  if (error) {
    return (
      <AuthSplitLayout>
        <div role="alert" className="space-y-6">
          <div className="flex size-11 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <CircleAlert className="size-5" aria-hidden="true" />
          </div>
          <p className="text-lg font-semibold">{error}</p>
          <Button
            onClick={() => {
              setError(null)
              void navigate({ to: '/auth', replace: true })
            }}
            className="h-[42px] w-full rounded-lg"
          >
            {t('onboarding.backToStart')}
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  if (!userEmail) {
    return (
      <AuthSplitLayout>
        <div role="status" className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          <span className="text-[15px]">{t('onboarding.loading')}</span>
        </div>
      </AuthSplitLayout>
    )
  }

  const isInviteFlow = Boolean(sessionStorage.getItem('pendingInviteToken'))
    || Boolean(localStorage.getItem('pendingInviteToken'))

  return <OnboardingWizard userEmail={userEmail} initialData={initialData} isInviteFlow={isInviteFlow} />
}
